import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { getPortalState, type PortalPublicState } from "@/lib/api";

interface PlatformContextType {
  portalState: PortalPublicState | null;
  loading: boolean;
  /** Non-null when the portal-state fetch failed (network error or server error). */
  error: string | null;
  isMaintenance: boolean;
  /** True when openDate is in the future (portal not yet started) */
  isComingSoon: boolean;
  /** True when portal is closed and closeDate is in the past */
  isClosed: boolean;
  /** Re-fetch portal state from the server immediately */
  refetch: () => void;
}

const PlatformContext = createContext<PlatformContextType>({
  portalState: null,
  loading: true,
  error: null,
  isMaintenance: false,
  isComingSoon: false,
  isClosed: false,
  refetch: () => {},
});

export function PlatformProvider({ children }: { children: React.ReactNode }) {
  const [portalState, setPortalState] = useState<PortalPublicState | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchState = React.useCallback(() => {
    setError(null);
    getPortalState()
      .then((state) => {
        if (state === null) {
          setError("Unable to reach the server. Please check your connection.");
        } else {
          setPortalState(state);
        }
      })
      .catch(() => {
        setError("An unexpected error occurred loading portal settings.");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  const now = new Date();

  // Coming soon: openDate is set and still in the future
  const isComingSoon =
    !loading &&
    portalState !== null &&
    portalState.openDate !== null &&
    new Date(portalState.openDate) > now;

  // Closed: closeDate is set and already passed (takes precedence over coming-soon)
  const isClosed =
    !loading &&
    portalState !== null &&
    portalState.closeDate !== null &&
    new Date(portalState.closeDate) < now;

  const isMaintenance =
    !loading &&
    portalState !== null &&
    portalState.maintenanceMode === true;

  return (
    <PlatformContext.Provider
      value={{
        portalState,
        loading,
        error,
        isMaintenance,
        isComingSoon,
        isClosed,
        refetch: fetchState,
      }}
    >
      {children}
    </PlatformContext.Provider>
  );
}

export function usePlatform() {
  return useContext(PlatformContext);
}
